import { useState } from 'react'
import {
  CircledIcon,
  Mtds,
  TopBar,
  AppLayout,
  ScreenHeader,
  QrCodeIcon,
  XIcon,
  useBulkUpdateSampleStatus,
  ProveScan,
  useAuth,
  SaveChangesBanner,
  useAllSamples,
  Sample,
  TestTubeIcon,
  OwnerId,
  getFirstRequisition,
  HashIcon,
  SampleShippingAddress,
  useErrorToast
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { useNavigate, useLocation } from 'react-router'
import { SideBar } from '../components/side-bar/SideBar'

export const SamplesToMarkAsSentScreen = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const userIdParam = params.get('userId') || undefined
  const ownerId = params.get('ownerId') || undefined
  const { userId } = useAuth()
  const showError = useErrorToast('merke prøvene som sendt')

  type StatusDto = { sampleId: string; status: string }
  type UpdateSamplesStatusDto = { statuses: StatusDto[] }

  const updateSampleStatus = useBulkUpdateSampleStatus()
  const navigate = useNavigate()

  const { samples, isLoading, isError, progress } = useAllSamples({
    isEnabled: true,
    ownerScope: 'subtree',
    ownerIds: ownerId ? [ownerId as unknown as OwnerId] : undefined,
    filters: {
      planIds: [],
      productIds: [],
      matrixIds: [],
      sampntTypeIds: [],
      substanceIds: [],
      status: ['SampleCreated'],
      userId: userIdParam
    }
  })

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isScanning, setIsScanning] = useState(false)

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
    )
  }

  const sampleScanned = (sampleId: string) => {
    if (!samples.some((sample: Sample) => sample.id === sampleId)) {
      Mtds.toast.danger(
        `Prøve med posenummer #${sampleId} finnes ikke i listen over prøver som kan merkes som sendt.`
      )
      return
    }
    handleCheckboxChange(sampleId, true)
  }

  const handleMarkSampleAsSent = async () => {
    const markedSamples = samples.filter((s: Sample) =>
      selectedIds.includes(s.id)
    )
    const newStatus = 'SampleSent'
    if (!userId) {
      Mtds.toast.danger('Bruker ikke logget inn')
      return
    }

    try {
      const data = (await updateSampleStatus.mutateAsync({
        sampleIds: markedSamples.map((s: Sample) => s.id),
        newStatus
      })) as UpdateSamplesStatusDto

      const updatedSamples: string[] = []
      data.statuses.forEach((d) => {
        if (d.status.startsWith('Failed')) {
          console.error(`Prøve #${d.sampleId} feilet med: ${d.status}`)
          Mtds.toast.danger(`Kunne ikke merke prøve #${d.sampleId} som sendt.`)
        } else {
          updatedSamples.push(d.sampleId)
        }
      })

      setSelectedIds([])

      if (updatedSamples.length > 0) {
        navigate('/samples-marked-as-sent', {
          state: { sampleIds: updatedSamples }
        })
      }
    } catch (e) {
      showError(e)
    }
  }

  const breadcrumbItems = [
    { label: 'Hjem', to: '/home' },
    { label: 'Merk prøver som sendt' }
  ]

  return (
    <AppLayout
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
      sidebar={<SideBar />}
      isLoading={isLoading}
      isError={isError}
      loadingMessage={'Laster inn prøver...'}
      loadingProgress={progress}
    >
      <Mtds.Grid data-justify='center' data-color='neutral'>
        <CircledIcon icon={<TestTubeIcon />} />
        <ScreenHeader title='Merk prøver som sendt' />
        <Mtds.Heading as='h2' data-size='xs' data-justify='center'>
          Merk prøvene som sendt samme dag du sender dem. Det er viktig at vi
          har riktige opplysninger for hver prøve.
        </Mtds.Heading>
      </Mtds.Grid>

      {samples.length > 0 ? (
        <>
          {isScanning ? (
            <>
              <ProveScan type='sample' onScan={sampleScanned} />
              <Mtds.Button
                onClick={() => setIsScanning(false)}
                data-variant='secondary'
              >
                <XIcon /> Avslutt skanning
              </Mtds.Button>
            </>
          ) : (
            <Mtds.Button
              data-variant='secondary'
              onClick={() => setIsScanning(true)}
            >
              <QrCodeIcon /> Skann for å merke
            </Mtds.Button>
          )}

          <Mtds.Table data-fixed>
            <thead>
              <tr>
                <th>Pose</th>
                <th>Prøve av</th>
                <th>Laboratorium</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {samples.map((sample: Sample) => {
                const requisition = getFirstRequisition(sample)
                return (
                  <tr key={sample.id}>
                    <td>
                      <Mtds.Flex data-align='center' data-gap='1'>
                        <HashIcon />
                        <strong>{sample.id}</strong>
                      </Mtds.Flex>
                    </td>
                    <td>{sample.sampleType.matrix}</td>
                    <td>
                      <Mtds.Grid data-gap='1'>
                        {requisition ? requisition.lab.name : '-'}
                        <SampleShippingAddress
                          address={sample.shippingAddress}
                          showName={false}
                        />
                      </Mtds.Grid>
                    </td>
                    <td>
                      <Mtds.Flex data-justify={'end'}>
                        <Mtds.Field
                          as='input'
                          type='checkbox'
                          id={sample.id}
                          aria-label={`Merk prøve ${sample.id} som sendt`}
                          checked={selectedIds.includes(sample.id)}
                          onChange={(e) =>
                            handleCheckboxChange(sample.id, e.target.checked)
                          }
                          data-command='row'
                        />
                      </Mtds.Flex>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Mtds.Table>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <p>Ingen prøver å merke for sending</p>
        </div>
      )}

      {selectedIds.length > 0 && (
        <SaveChangesBanner
          onSave={handleMarkSampleAsSent}
          onDiscard={() => setSelectedIds([])}
          saveButtonLabel='Merk som sendt'
          discardButtonLabel={'Fjern'}
          headerText={`Du har valgt ${selectedIds.length} ${selectedIds.length === 1 ? 'prøve' : 'prøver'} som skal merkes som sendt`}
        />
      )}
    </AppLayout>
  )
}
